import { 
    collection, 
    addDoc, 
    serverTimestamp, 
    query, 
    where, 
    getDocs, 
    writeBatch,
    doc
} from 'firebase/firestore';
import { db } from '../firebase/client';
import { getCompanyId } from '../tenantContext';

export interface Notification {
    id?: string;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'alert' | 'success';
    severity?: 'low' | 'medium' | 'high' | 'critical';
    target?: 'all' | 'managers' | 'pumpers' | 'specific';
    targetUserId?: string;
    read: boolean;
    dismissed: boolean;
    createdAt: any;
    companyId: string;
}

export const notificationService = {
    /**
     * Broadcast an alert to all users in the tenant
     */
    async broadcastAlert(
        title: string, 
        message: string, 
        severity: 'low' | 'medium' | 'high' | 'critical' = 'medium'
    ) {
        const companyId = getCompanyId();
        if (!companyId) throw new Error('No tenant ID found');

        // 1. Create a global alert record (optional, for history)
        const alertsRef = collection(db, `companies/${companyId}/alerts`);
        await addDoc(alertsRef, {
            title,
            message,
            severity,
            createdAt: serverTimestamp(),
            createdBy: 'manager', // TODO: Add actual user ID
            target: 'all'
        });

        // 2. Fan-out to all users (or use a cloud function for this if > 100 users)
        // For now, we'll just create a notification for each user we can find
        // A better approach for scale is to have the client listen to a 'global_alerts' collection
        
        // Fetch all users
        const usersRef = collection(db, `companies/${companyId}/users`);
        const usersSnapshot = await getDocs(usersRef);
        
        const batch = writeBatch(db);
        let operationCount = 0;

        usersSnapshot.docs.forEach(userDoc => {
            const notifRef = doc(collection(db, `companies/${companyId}/users/${userDoc.id}/notifications`));
            batch.set(notifRef, {
                title,
                message,
                type: severity === 'critical' ? 'alert' : 'warning',
                severity,
                read: false,
                dismissed: false,
                createdAt: serverTimestamp(),
                companyId
            });
            operationCount++;
        });

        if (operationCount > 0) {
            await batch.commit();
        }
        
        return operationCount;
    },

    /**
     * Send a notification to a specific user
     */
    async sendNotification(
        userId: string,
        title: string,
        message: string,
        type: 'info' | 'warning' | 'alert' | 'success' = 'info'
    ) {
        const companyId = getCompanyId();
        if (!companyId) throw new Error('No tenant ID found');

        const notifRef = collection(db, `companies/${companyId}/users/${userId}/notifications`);
        await addDoc(notifRef, {
            title,
            message,
            type,
            read: false,
            dismissed: false,
            createdAt: serverTimestamp(),
            companyId
        });
    }
};
